import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Textarea,
  cn,
} from "../../shared/ui";
import type { WorkItemDetail } from "../../shared/types/api";
import { formatHours } from "./workItemVocabulary";

/**
 * Re-estimates an item (§55).
 *
 * <p>The original estimate is shown and is not a field. §55 says it must never be silently
 * modified, and the reason is visible right here: the difference between it and the new figure is
 * the estimate variance, and a form offering to edit both would let somebody make a task that
 * doubled look like one estimated correctly from the start.
 */
export function ReestimateDrawer({
  item,
  onClose,
  onSaved,
}: {
  item: WorkItemDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [current, setCurrent] = useState("");
  const [remaining, setRemaining] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setCurrent(
        item.currentEstimateHours != null
          ? String(item.currentEstimateHours)
          : "",
      );
      setRemaining(
        item.remainingEstimateHours != null
          ? String(item.remainingEstimateHours)
          : "",
      );
      setReason("");
      setError(null);
    }
  }, [item]);

  /*
   * A number that has not moved needs no explanation, and asking for one would teach people to
   * type "n/a" — which reads as evidence and is not. Both figures are compared as text, so
   * "8" and "8.0" do not register as a change nobody made.
   */
  const changed =
    item != null &&
    (current !==
      (item.currentEstimateHours != null
        ? String(item.currentEstimateHours)
        : "") ||
      remaining !==
        (item.remainingEstimateHours != null
          ? String(item.remainingEstimateHours)
          : ""));

  /*
   * No baseline yet, so this is the first estimate rather than a re-estimate. It goes through the
   * plan endpoint, which records it as the original the server's re-estimate refuses to invent,
   * and it asks no reason: a figure written down for the first time has not moved from anything.
   *
   * Without this an item created with dates but no estimate had no way forward — selecting it for
   * development needs an estimate, re-estimating needs an original, and the dates panel only
   * offers an estimate to an item with no dates.
   */
  const firstEstimate = item != null && item.originalEstimateHours == null;

  const submitFirst = async () => {
    if (!item) {
      return;
    }
    if (current === "" || Number(current) <= 0) {
      setError("Give the estimate in hours.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/work-items/${item.id}/plan`, {
        originalEstimateHours: Number(current),
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save the estimate.");
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!item) {
      return;
    }
    if (firstEstimate) {
      await submitFirst();
      return;
    }
    if (changed && reason.trim() === "") {
      setError(
        "Say why the estimate is changing. The figures show that it moved; these words say what " +
          "happened.",
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/work-items/${item.id}/estimate`, {
        currentEstimateHours: current === "" ? null : Number(current),
        remainingEstimateHours: remaining === "" ? null : Number(remaining),
        reason: reason || null,
      });
      onSaved();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not save the estimate.",
      );
    } finally {
      setSaving(false);
    }
  };

  const original = item?.originalEstimateHours ?? null;
  const proposed = current === "" ? null : Number(current);
  const variance =
    original != null && proposed != null ? proposed - original : null;

  return (
    <Drawer
      open={Boolean(item)}
      onClose={onClose}
      title={firstEstimate ? "Estimate" : "Re-estimate"}
      subtitle={item ? `${item.itemKey} — ${item.title}` : undefined}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving}>
            {saving ? "Saving…" : "Save estimate"}
          </Button>
        </div>
      }
    >
      {firstEstimate ? (
        <div className="space-y-4">
          {error && <InlineAlert tone="error">{error}</InlineAlert>}
          <Field
            label="Estimate (hours)"
            required
            hint="Recorded as the original estimate. Later changes are re-estimates, measured against it."
          >
            <Input
              type="number"
              min={0}
              step="0.5"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </Field>
        </div>
      ) : (
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <div className="flex items-center justify-between rounded-lg border border-ink-200 bg-ink-50 px-3 py-2.5">
          <span className="text-sm text-ink-500">Originally estimated</span>
          <span className="tnum text-sm font-medium text-ink-900">
            {formatHours(original)}
          </span>
        </div>

        <InlineAlert tone="info">
          The original stays exactly as it is. The gap between it and the figure
          below is the estimate variance every forecast is built from — which is
          the reason it cannot be edited.
        </InlineAlert>

        <div className="grid grid-cols-2 gap-4">
          <Field
            label="Current estimate (hours)"
            hint="What the whole job now looks like."
          >
            <Input
              type="number"
              min={0}
              step="0.5"
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </Field>
          <Field label="Remaining (hours)" hint="What is still left to do.">
            <Input
              type="number"
              min={0}
              step="0.5"
              value={remaining}
              onChange={(event) => setRemaining(event.target.value)}
            />
          </Field>
        </div>

        {variance != null && variance !== 0 && (
          <div
            className={cn(
              "rounded-lg border px-3 py-2.5 text-sm",
              variance > 0
                ? "border-red-200 bg-red-50 text-red-700"
                : "border-emerald-200 bg-emerald-50 text-emerald-700",
            )}
          >
            That is{" "}
            <span className="tnum font-medium">
              {variance > 0 ? "+" : "−"}
              {formatHours(Math.abs(variance))}
            </span>{" "}
            against the original estimate of {formatHours(original)}
            {variance > 0
              ? ", and it will show on the project's effort variance."
              : ". The original stays on record either way."}
          </div>
        )}

        <Field
          label="Why"
          required={changed}
          hint={
            changed
              ? "The figures show that the estimate moved; only these words say what happened."
              : "Asked for once a figure above actually changes."
          }
        >
          <Textarea
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Two extra payment providers were added to the scope."
          />
        </Field>
      </div>
      )}
    </Drawer>
  );
}
